import { useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

// ─── Event name constants ───
export const ANALYTICS_EVENTS = {
  // Landing & navigation
  PAGE_VIEW_HOME: "page_view_home",
  CTA_CLICK: "cta_click",

  // Wizard funnel
  WIZARD_STARTED: "wizard_started",
  WIZARD_DESTINATION_SET: "wizard_destination_set",
  WIZARD_VEHICLE_SET: "wizard_vehicle_set",
  WIZARD_DATES_SET: "wizard_dates_set",
  WIZARD_START_SET: "wizard_start_set",
  WIZARD_STOP_ADDED: "wizard_stop_added",
  WIZARD_ROUTE_CALCULATED: "wizard_route_calculated",
  WIZARD_TRIP_GENERATED: "wizard_trip_generated",
  WIZARD_STEP_COMPLETED: "wizard_step_completed",

  // Aha moments
  FIRST_PLAN_CREATED: "first_plan_created",
  FIRST_STOP_ADDED: "first_stop_added",
  FIRST_BUDGET_SET: "first_budget_set",
  MAP_VIEWED: "map_viewed",

  // Save & auth
  SAVE_TRIP_CLICK: "save_trip_click",
  TRIP_SAVED: "trip_saved",
  AUTH_GATE_SHOWN: "auth_gate_shown",
  AUTH_GATE_SIGNUP_CLICK: "auth_gate_signup_click",
  AUTH_GATE_LOGIN_CLICK: "auth_gate_login_click",
  AUTH_GATE_DISMISSED: "auth_gate_dismissed",
  AUTH_GATE_TRIGGER: "auth_gate_trigger",
  GUEST_SIGNUP: "guest_signup",
  GUEST_LOGIN: "guest_login",
  TRIP_RESTORED_AFTER_AUTH: "trip_restored_after_auth",

  // Active trip
  TRIP_STARTED: "trip_started",
  TRIP_COMPLETED: "trip_completed",
  TRIP_RESTORED: "trip_restored",

  // Sharing
  TRIP_SHARED: "trip_shared",
  SHARED_TRIP_OPENED: "shared_trip_opened",
  PUBLIC_TRIP_OPENED: "public_trip_opened",

  // Engagement
  CHECKLIST_INTERACTION: "checklist_interaction",
  BUDGET_INTERACTION: "budget_interaction",
  INCIDENT_CREATED: "incident_created",

  // Notifications
  NOTIFICATION_SHOWN: "notification_shown",
  NOTIFICATION_CLICKED: "notification_clicked",
  NOTIFICATION_DISMISSED: "notification_dismissed",

  // Retention
  USER_RETURN: "user_return",
  NEXT_STEP_SHOWN: "next_step_shown",
  NEXT_STEP_CLICKED: "next_step_clicked",
} as const;

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export interface AnalyticsProperties {
  trip_id?: string;
  vehicle_type?: string;
  country?: string;
  language?: string;
  is_guest?: boolean;
  source_page?: string;
  cta_label?: string;
  destination?: string;
  step?: string;
  context?: string;
  [key: string]: string | number | boolean | null | undefined;
}

function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function getSessionId(): string {
  const key = "vt_analytics_sid";
  let sid = sessionStorage.getItem(key);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(key, sid);
  }
  return sid;
}

// Deduplication: prevent same event within N ms
const recentEvents = new Map<string, number>();
const DEDUP_WINDOW_MS = 2000;

function isDuplicate(event: string, dedupKey?: string): boolean {
  const key = dedupKey || event;
  const now = Date.now();
  const last = recentEvents.get(key);
  if (last && now - last < DEDUP_WINDOW_MS) return true;
  recentEvents.set(key, now);
  // Cleanup old entries
  if (recentEvents.size > 100) {
    for (const [k, v] of recentEvents) {
      if (now - v > DEDUP_WINDOW_MS * 5) recentEvents.delete(k);
    }
  }
  return false;
}

/**
 * Central analytics tracking hook.
 * Usage: const { track } = useAnalytics();
 *        track(ANALYTICS_EVENTS.WIZARD_STARTED, { source_page: "/trips" });
 */
export function useAnalytics() {
  const { user } = useAuth();
  const trackingRef = useRef(false);

  const track = useCallback(
    async (event: AnalyticsEvent | string, properties: AnalyticsProperties = {}) => {
      // Dedup check
      const dedupKey = `${event}:${properties.trip_id || ""}:${properties.step || ""}`;
      if (isDuplicate(event, dedupKey)) return;

      // Enrich with defaults
      const enriched: AnalyticsProperties = {
        ...properties,
        is_guest: properties.is_guest ?? !user,
        language: properties.language ?? (document.documentElement.lang || "sv"),
      };

      try {
        await supabase.from("analytics_events").insert({
          event_name: event,
          properties: enriched as any,
          session_id: getSessionId(),
          user_id: user?.id ?? null,
          device_type: getDeviceType(),
          page_path: window.location.pathname,
          referrer: document.referrer || null,
        } as any);
      } catch {
        // silent – analytics must never break the app
      }
    },
    [user]
  );

  // Track once per mount (useful for page views)
  const trackOnce = useCallback(
    (event: AnalyticsEvent | string, properties: AnalyticsProperties = {}) => {
      if (trackingRef.current) return;
      trackingRef.current = true;
      track(event, properties);
    },
    [track]
  );

  return { track, trackOnce };
}
